/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_URL, SUPPRESS_GLOBAL_ERROR } from '../tokens';
import { Observable } from 'rxjs';

interface QueryParams {
  [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> | undefined | null;
}

export interface ApiError {
  status?: number;
  title?: string;
  name?: string;
  message?: string;
  error?: {
    title?: string;
    detail?: string;
    exception?: string;
    instance?: string;
    stackTrace?: string;
    traceId?: string;
    type?: string;
  };
}

/**
 * Wrapper around HttpClient as to provide needed headers and base url for all requests.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(
    private http: HttpClient,
    @Inject(API_URL) private apiUrl: string,
  ) {}

  get<T>(
    path: string,
    query?: QueryParams,
    headers?: { [header: string]: string | string[] },
    suppressError?: boolean,
  ) {
    const qp = this.createQueryParamsString(query);

    let ctx = new HttpContext();
    if (suppressError) ctx = ctx.set(SUPPRESS_GLOBAL_ERROR, true);

    return this.http.get<T>(`${this.apiUrl}${path}${qp}`, { headers, context: ctx });
  }

  getBlob(path: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}${path}`, { responseType: 'blob' });
  }

  post<T>(
    path: string,
    body: object,
    query?: QueryParams,
    headers?: { [header: string]: string | string[] },
  ) {
    const qp = this.createQueryParamsString(query);
    return this.http.post<T>(`${this.apiUrl}${path}${qp}`, body, { headers });
  }

  postBlob(
    path: string,
    body: object,
    query?: QueryParams,
    headers?: { [header: string]: string | string[] },
  ): Observable<HttpResponse<Blob>> {
    const qp = this.createQueryParamsString(query);
    return this.http.post(`${this.apiUrl}${path}${qp}`, body, {
      headers,
      responseType: 'blob',
      observe: 'response',
    });
  }

  put<T>(path: string, body: object, query?: QueryParams, headers?: { [header: string]: string | string[] }) {
    const qp = this.createQueryParamsString(query);
    return this.http.put<T>(`${this.apiUrl}${path}${qp}`, body, { headers });
  }

  patch<T>(
    path: string,
    body: object,
    query?: QueryParams,
    headers?: { [header: string]: string | string[] },
  ) {
    const qp = this.createQueryParamsString(query);
    return this.http.patch<T>(`${this.apiUrl}${path}${qp}`, body, { headers });
  }

  delete<T>(path: string, query?: QueryParams, headers?: { [header: string]: string | string[] }) {
    const qp = this.createQueryParamsString(query);
    return this.http.delete<T>(`${this.apiUrl}${path}${qp}`, { headers });
  }

  private createQueryParamsString(query?: QueryParams) {
    if (query && Object.keys(query).length > 0) {
      // remove null and undefined values
      Object.keys(query).forEach(
        (key) => (query[key] === undefined || query[key] === null) && delete query[key],
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const qp = new HttpParams({ fromObject: query as any }).toString();
      return `?${qp}`;
    }
    return '';
  }
}
